import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type {
  LocationFeatureItemResolved,
  LocationFeaturesResolved,
} from './types';
import { Collapsible } from '@/components/Collapsible';
import { Icon } from '@/components/Icon';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { sanitizeLinksOnly } from '@/lib/sanitize';

/** An entry whose optional description holds more than whitespace. */
const hasDescription = (item: LocationFeatureItemResolved): boolean =>
  item.description.trim() !== '';

export const LocationFeaturesWidgetComponent = ({
  resolved,
  className,
}: {
  resolved: LocationFeaturesResolved;
  /**
   * Extra classes from a campaign variant wrapper. Variants B and C use it for
   * their top margin (SH-415); variant A passes nothing and is unchanged.
   */
  className?: string;
}) => {
  const sub = useSubstitute();
  const items = (resolved.items ?? []).slice(0, 3);
  if (items.length === 0) {
    return null;
  }

  return (
    <section
      // `pb-4` is an exception to the top-padding-only convention
      // (CLAUDE.md): this widget paints its own background from `md`, so its
      // last row must not sit flush against the band's bottom edge.
      className={classNames(
        'md:bg-secondary-lighter bg-white pt-4 pb-4',
        className,
      )}
    >
      {/* The band is full-bleed; its *content* still sits in the ordinary
          content column, so the icons line up with the widgets above and
          below (70.14-Widget-Width § 4). Was `px-2`, an unflagged straggler
          that put this widget 8px off from its neighbours. */}
      <div className="mx-auto max-w-6xl px-4 lg:px-9">
        <div className="hidden items-start gap-4 md:grid md:grid-cols-3">
          {items.map((item, i) => (
            <div
              key={`desktop-icons-${item.title}-${i}`}
              className="flex items-center gap-4"
            >
              <div className="border-card-border text-foreground/50 mt-3 flex h-16 w-16 shrink-0 items-center justify-center rounded-full border bg-stone-200">
                <Icon src={item.iconDesktop} size={40} />
              </div>
              {hasDescription(item) ? (
                <div>
                  <p className="h5">{sub(item.title)}</p>
                  <div
                    className="[&_a]:text-primary mt-1 text-base leading-5.5"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeLinksOnly(sub(item.description)),
                    }}
                  />
                </div>
              ) : (
                // A title-only entry is one plain line, not a heading — the
                // Standort-Jobs USP strip (10.03.02.01.04 § AC 2).
                <p className="m-0 mt-3 text-base">{sub(item.title)}</p>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-3 md:hidden">
          {items.map((item, i) =>
            // Nothing to expand without a description: a plain icon + text row.
            !hasDescription(item) ? (
              <div
                key={`mobile-icons-${item.title}-${i}`}
                className="flex items-center gap-3 py-1"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                  <Icon src={item.iconMobile || item.iconDesktop} size={24} />
                </span>
                <span className="text-sm">{sub(item.title)}</span>
              </div>
            ) : (
              <Collapsible
                key={`mobile-icons-${item.title}-${i}`}
                header={
                  <span className="flex items-center gap-3 py-1">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                      <Icon src={item.iconMobile} size={24} />
                    </span>
                    <span className="text-sm font-medium">
                      {sub(item.title)}
                    </span>
                  </span>
                }
              >
                <div
                  className="[&_a]:text-primary text-sm leading-5 [&_a]:underline"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLinksOnly(sub(item.description)),
                  }}
                />
              </Collapsible>
            ),
          )}
        </div>
      </div>
    </section>
  );
};
